import { useDraftStore } from '../../stores/draftStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { DraftEditor } from './DraftEditor';
import { DraftActions } from './DraftActions';
import { ToneSelector } from './ToneSelector';
import { UrgencyBadge, ConfidenceBadge } from '../common/Badge';
import { Card } from '../common/Card';
import {
  Warning24Regular,
  Attach24Regular,
  PersonArrowRight24Regular,
} from '@fluentui/react-icons';
import type { ToneType } from '../../types/index';

export function DraftPanel() {
  const { draft, analysis, summary, selectedTone, setSelectedTone } = useDraftStore();
  const { defaultTone } = useSettingsStore();

  const currentTone = selectedTone || defaultTone;

  if (!draft) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-md)',
        height: '100%',
      }}
    >
      {/* Summary & Analysis */}
      {summary && (
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            {/* Email Summary */}
            <div>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'var(--color-gray-500)',
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Summary
              </div>
              <div style={{ color: 'var(--color-gray-700)' }}>{summary}</div>
            </div>

            {/* Analysis badges */}
            {analysis && (
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
                <UrgencyBadge level={analysis.urgency} />
                <ConfidenceBadge confidence={analysis.confidence} />
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Warnings & Flags */}
      {analysis && (analysis.missingAttachments || analysis.escalation || analysis.lowConfidenceReasons.length > 0) && (
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            {/* Missing Attachments Warning */}
            {analysis.missingAttachments && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--spacing-sm)',
                  padding: 'var(--spacing-sm)',
                  backgroundColor: 'var(--color-warning)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'white',
                }}
              >
                <Attach24Regular />
                <div>
                  <div style={{ fontWeight: 500 }}>Missing Attachment</div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>
                    {analysis.attachmentNote || 'The email references an attachment that was not found.'}
                  </div>
                </div>
              </div>
            )}

            {/* Escalation Suggestion */}
            {analysis.escalation && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--spacing-sm)',
                  padding: 'var(--spacing-sm)',
                  backgroundColor: 'var(--color-info)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'white',
                }}
              >
                <PersonArrowRight24Regular />
                <div>
                  <div style={{ fontWeight: 500 }}>Escalation Suggested</div>
                  <div style={{ fontSize: '12px', opacity: 0.9 }}>
                    Consider routing to {analysis.escalation.target}: {analysis.escalation.reason}
                  </div>
                </div>
              </div>
            )}

            {/* Low Confidence Warning */}
            {analysis.lowConfidenceReasons.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--spacing-sm)',
                  padding: 'var(--spacing-sm)',
                  backgroundColor: 'var(--color-gray-100)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <Warning24Regular style={{ color: 'var(--color-warning)' }} />
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--color-gray-700)' }}>
                    Review Recommended
                  </div>
                  <ul
                    style={{
                      fontSize: '12px',
                      color: 'var(--color-gray-600)',
                      margin: '4px 0 0 0',
                      paddingLeft: '16px',
                    }}
                  >
                    {analysis.lowConfidenceReasons.map((reason, i) => (
                      <li key={i}>{reason}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Tone Selector */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ToneSelector
          value={currentTone}
          onChange={(tone: ToneType) => setSelectedTone(tone)}
        />
      </div>

      {/* Draft Editor */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <DraftEditor />
      </div>

      {/* Actions */}
      <DraftActions />
    </div>
  );
}
